/**
 * KUA Admin Interface
 * Simple JavaScript application to interact with the KUA API
 */

// Import dependencies
import KuaApiAdapter from './api';
import * as utils from './utils';

// Initialize API client
let kuaApi = null;

// Application state
let state = {
  apiUrl: localStorage.getItem('kuaApiUrl') || '',
  users: [],
  groups: [],
  currentTab: 'users'
};

// DOM Elements
const apiUrlInput = document.getElementById('api-url');
const saveUrlBtn = document.getElementById('save-url');
const tabButtons = document.querySelectorAll('.tab-btn');
const tabContents = document.querySelectorAll('.tab-content');

// User tab elements
const refreshUsersBtn = document.getElementById('refresh-users');
const addUserBtn = document.getElementById('add-user');
const userList = document.getElementById('user-list');

// Groups tab elements removed

// Permissions tab elements
const permEmailInput = document.getElementById('perm-email');
const getPermissionsBtn = document.getElementById('get-permissions');
const permissionsList = document.getElementById('permissions-list');
const groupKeySelect = document.getElementById('group-key');
const customGroupKeyInput = document.getElementById('custom-group-key');
const groupValuesInput = document.getElementById('group-values');
const addGroupBtn = document.getElementById('add-group');
const getExistingGroupsBtn = document.getElementById('get-existing-groups');
const currentGroupsDiv = document.getElementById('current-groups');
const permissionModuleInput = document.getElementById('permission-module');
const addPermissionBtn = document.getElementById('add-permission');

// Permission discovery tool elements
const discoverPermissionInput = document.getElementById('discover-permission');
const discoverPermissionBtn = document.getElementById('discover-permission-btn');
const permissionDiscoveryResult = document.getElementById('permission-discovery-result');

// Permission comparison tool elements
const compareEmailInput = document.getElementById('compare-email');
const comparePermissionsBtn = document.getElementById('compare-permissions-btn');
const permissionsComparisonResult = document.getElementById('permissions-comparison-result');

// Modal elements
const userModal = document.getElementById('user-modal');
const closeModal = document.querySelector('.close');
const userForm = document.getElementById('user-form');
const modalTitle = document.getElementById('modal-title');
const userNameInput = document.getElementById('user-name');
const userEmailInput = document.getElementById('user-email');

// Initialize application
function init() {
  // Set API URL from local storage if available
  if (state.apiUrl) {
    apiUrlInput.value = state.apiUrl;
    try {
      kuaApi = new KuaApiAdapter(state.apiUrl);
      console.log('KUA API initialized with URL:', state.apiUrl);
      // Update the current tab in state to match the active tab
      state.currentTab = 'permissions';
    } catch (error) {
      console.error('Error initializing API adapter:', error);
      utils.notify('Error initializing API adapter. Please check the API URL.', 'error');
    }
  } else {
    console.log('No API URL found in localStorage. Please set one.');
  }

  // Setup event listeners
  setupEventListeners();
  
  // Set up event listeners for permission tags separately to avoid duplication
  document.querySelectorAll('.permission-tag').forEach(tag => {
    // Remove any existing click listeners first
    tag.removeEventListener('click', handlePermissionTagClick);
    // Then add the listener
    tag.addEventListener('click', handlePermissionTagClick);
  });
  
  // Check API initialization status
  window.addEventListener('focus', checkApiStatus);
}

// Handle permission tag click to avoid duplicate event binding
function handlePermissionTagClick(event) {
  const module = event.currentTarget.dataset.module;
  permissionModuleInput.value = module;
  addUserPermission();
}

function setupEventListeners() {
  // API URL configuration
  saveUrlBtn.addEventListener('click', saveApiUrl);
  
  // Theme toggle button
  const themeToggleBtn = document.getElementById('theme-toggle');
  if (themeToggleBtn) {
    // Check if user has a saved theme preference
    const savedTheme = localStorage.getItem('kuaAdminTheme');
    if (savedTheme === 'dark') {
      document.body.classList.add('dark-theme');
      updateThemeToggleButton(true);
    }
    
    themeToggleBtn.addEventListener('click', toggleTheme);
  }
  
  // Tab navigation
  tabButtons.forEach(btn => {
    btn.addEventListener('click', () => switchTab(btn.dataset.tab));
  });
  
  // User tab events
  refreshUsersBtn.addEventListener('click', loadUserData);
  addUserBtn.addEventListener('click', showAddUserModal);
  
  // Groups tab events removed
  
  // Permissions tab events
  getPermissionsBtn.addEventListener('click', loadUserPermissions);
  groupKeySelect.addEventListener('change', handleGroupKeyChange);
  addGroupBtn.addEventListener('click', addUserGroup);
  getExistingGroupsBtn.addEventListener('click', loadUserGroupData);
  addPermissionBtn.addEventListener('click', addUserPermission);
  
  // Add event listeners to permission tags
  // We'll add these in the init function to avoid duplicates
  
  // Permission discovery tool
  if (discoverPermissionBtn) {
    discoverPermissionBtn.addEventListener('click', discoverPermissionMapping);
  }
  
  // Permission comparison tool - will initialize these properly
  if (document.getElementById('compare-permissions-btn')) {
    document.getElementById('compare-permissions-btn').addEventListener('click', compareUserPermissions);
  }
  
  // Modal events
  closeModal.addEventListener('click', () => userModal.style.display = 'none');
  userForm.addEventListener('submit', handleUserFormSubmit);
  
  // Close modal when clicking outside
  window.addEventListener('click', (e) => {
    if (e.target === userModal) {
      userModal.style.display = 'none';
    }
  });
}

// API URL functions
function saveApiUrl() {
  const url = apiUrlInput.value.trim();
  if (!url) {
    utils.notify('Please enter a valid API URL', 'error');
    return;
  }
  
  state.apiUrl = url;
  localStorage.setItem('kuaApiUrl', url);
  
  try {
    // Initialize API client with new URL
    kuaApi = new KuaApiAdapter(url);
    console.log('KUA API initialized with URL:', url);
    
    utils.notify('API URL saved!', 'success');
    
    // Reload data with new URL
    loadUserData();
  } catch (error) {
    console.error('Error initializing API adapter:', error);
    utils.notify('Error initializing API adapter. Please check the API URL.', 'error');
  }
}

// Function to check API status
function checkApiStatus() {
  if (!kuaApi && state.apiUrl) {
    try {
      kuaApi = new KuaApiAdapter(state.apiUrl);
      console.log('KUA API re-initialized with URL:', state.apiUrl);
    } catch (error) {
      console.error('Error re-initializing API adapter:', error);
    }
  }
}

// Tab functions
function switchTab(tabName) {
  state.currentTab = tabName;
  
  // Update active tab button
  tabButtons.forEach(btn => {
    btn.classList.toggle('active', btn.dataset.tab === tabName);
  });
  
  // Show active tab content
  tabContents.forEach(content => {
    if (content.id === `${tabName}-tab`) {
      content.classList.add('active');
    } else {
      content.classList.remove('active');
    }
  });
  
  // Load data for the selected tab
  if (tabName === 'users') loadUserData();
}

// User functions
async function loadUserData() {
  if (!kuaApi) {
    utils.notify('Please set the API URL first', 'error');
    return;
  }
  
  try {
    userList.innerHTML = '<tr><td colspan="4">Loading...</td></tr>';
    
    const users = await kuaApi.getUsers();
    state.users = users;
    renderUserList();
  } catch (error) {
    console.error('Error loading users:', error);
    userList.innerHTML = `<tr><td colspan="4">Error: ${error.message}</td></tr>`;
    utils.notify(`Error loading users: ${error.message}`, 'error');
  }
}

function renderUserList() {
  if (!state.users.length) {
    userList.innerHTML = '<tr><td colspan="4">No users found</td></tr>';
    return;
  }
  
  const html = state.users.map(user => `
    <tr>
      <td>${user.name || '-'}</td>
      <td>${user.email}</td>
      <td>${utils.formatDate(user.expired_at)}</td>
      <td class="actions">
        <button class="edit-btn" data-email="${user.email}">Edit</button>
        <button class="delete-btn" data-email="${user.email}">Delete</button>
      </td>
    </tr>
  `).join('');
  
  userList.innerHTML = html;
  
  // Add event listeners to action buttons
  document.querySelectorAll('.edit-btn').forEach(btn => {
    btn.addEventListener('click', () => showEditUserModal(btn.dataset.email));
  });
  
  document.querySelectorAll('.delete-btn').forEach(btn => {
    btn.addEventListener('click', () => deleteUser(btn.dataset.email));
  });
}

function showAddUserModal() {
  modalTitle.textContent = 'Add User';
  userNameInput.value = '';
  userEmailInput.value = '';
  userEmailInput.readOnly = false; // Allow email editing for new users
  userModal.style.display = 'block';
}

function showEditUserModal(email) {
  const user = state.users.find(u => u.email === email);
  if (!user) return;
  
  modalTitle.textContent = 'Edit User';
  userNameInput.value = user.name || '';
  userEmailInput.value = user.email;
  userEmailInput.readOnly = true; // Don't allow email editing for existing users
  
  userModal.style.display = 'block';
}

async function handleUserFormSubmit(e) {
  e.preventDefault();
  
  if (!kuaApi) {
    utils.notify('Please set the API URL first', 'error');
    return;
  }
  
  const isEdit = modalTitle.textContent === 'Edit User';
  const name = userNameInput.value.trim();
  const email = userEmailInput.value.trim();
  
  if (!name || !email) {
    utils.notify('Name and email are required', 'error');
    return;
  }
  
  if (!utils.isValidEmail(email)) {
    utils.notify('Please enter a valid email address', 'error');
    return;
  }
  
  const userData = { name, email };
  
  try {
    if (isEdit) {
      // Update existing user
      await kuaApi.updateUser(userData);
      utils.notify('User updated successfully!', 'success');
    } else {
      // Create new user
      await kuaApi.createUser(userData);
      utils.notify('User added successfully!', 'success');
    }
    
    userModal.style.display = 'none';
    loadUserData(); // Refresh user list
  } catch (error) {
    console.error('Error saving user:', error);
    utils.notify(`Error: ${error.message}`, 'error');
  }
}

async function deleteUser(email) {
  if (!confirm(`Are you sure you want to delete user ${email}?`)) return;
  
  if (!kuaApi) {
    utils.notify('Please set the API URL first', 'error');
    return;
  }
  
  utils.notify('Delete user functionality is not supported by the API', 'error');
  // API doesn't have a delete endpoint based on the route list
  // If you want to implement user deletion, you would need to add that endpoint to the Laravel backend
}

// Group functions removed

// Permission functions
async function loadUserPermissions() {
  const email = permEmailInput.value.trim();
  if (!email) {
    utils.notify('Please enter a user email', 'error');
    return;
  }
  
  if (!utils.isValidEmail(email)) {
    utils.notify('Please enter a valid email address', 'error');
    return;
  }
  
  if (!kuaApi) {
    utils.notify('Please set the API URL first', 'error');
    return;
  }
  
  // Disable the get permissions button to prevent multiple submissions
  getPermissionsBtn.disabled = true;
  
  try {
    permissionsList.innerHTML = '<p>Loading permissions...</p>';
    currentGroupsDiv.innerHTML = '<p>Loading group data...</p>';
    
    // Use Promise.all to fetch permissions and group data in parallel
    const [permissions] = await Promise.all([
      kuaApi.getUserPermissions(email),
      loadUserGroupData(email, false) // Pass email and a flag to not show notifications
    ]);
    
    renderPermissionsList(permissions);
    
    // Refresh click listeners on permission tags to ensure they use the current user
    document.querySelectorAll('.permission-tag').forEach(tag => {
      // Remove any existing listeners first
      tag.removeEventListener('click', handlePermissionTagClick);
      // Then add the listener back
      tag.addEventListener('click', handlePermissionTagClick);
    });
    
    utils.notify(`Permissions for ${email} loaded successfully`, 'success');
  } catch (error) {
    console.error('Error loading permissions:', error);
    permissionsList.innerHTML = `<p>Error: ${error.message}</p>`;
    utils.notify(`Error loading permissions: ${error.message}`, 'error');
  } finally {
    // Re-enable the button
    getPermissionsBtn.disabled = false;
  }
}

function renderPermissionsList(permissions) {
  if (!permissions || Object.keys(permissions).length === 0) {
    permissionsList.innerHTML = '<div class="empty-permissions"><p>No permissions found</p><p class="hint">Add permissions using the tools below</p></div>';
    return;
  }
  
  const html = Object.entries(permissions).map(([key, perm]) => `
    <div class="permission-item">
      <h3>${perm.name || key}</h3>
      <p>Canonical: <span class="canonical-value">${perm.canonical || key}</span></p>
      <button class="remove-permission" data-permission="${key}">Remove</button>
    </div>
  `).join('');
  
  permissionsList.innerHTML = html;
  
  // Add event listeners to remove buttons
  document.querySelectorAll('.remove-permission').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const permission = e.target.dataset.permission;
      removeUserPermission(permission);
      e.preventDefault();
    });
  });
}

// Group management functions
function handleGroupKeyChange() {
  const selectedValue = groupKeySelect.value;
  
  if (selectedValue === 'custom') {
    customGroupKeyInput.style.display = 'block';
  } else {
    customGroupKeyInput.style.display = 'none';
  }
}

async function loadUserGroupData(providedEmail = null, showNotifications = true) {
  const email = providedEmail || permEmailInput.value.trim();
  if (!email) {
    if (showNotifications) utils.notify('Please enter a user email first', 'error');
    return;
  }
  
  if (!utils.isValidEmail(email)) {
    if (showNotifications) utils.notify('Please enter a valid email address', 'error');
    return;
  }
  
  if (!kuaApi) {
    if (showNotifications) utils.notify('Please set the API URL first', 'error');
    return;
  }
  
  if (getExistingGroupsBtn && providedEmail === null) {
    getExistingGroupsBtn.disabled = true;
  }
  
  currentGroupsDiv.innerHTML = '<p>Loading group data...</p>';
  
  try {
    // For demonstration purposes, we'll show group data for some common keys
    // In a real implementation, you'd need an API to list all group keys for a user
    const commonKeys = ['azure_ad_group', 'ldap_group', 'rig_uuid'];
    const groupDataPromises = commonKeys.map(key => kuaApi.getGroupDataValues(email, key));
    
    const results = await Promise.allSettled(groupDataPromises);
    
    let html = '';
    let hasData = false;
    
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value && result.value.length > 0) {
        hasData = true;
        const key = commonKeys[index];
        const values = result.value;
        
        html += `
          <div class="group-entry" data-key="${key}">
            <div class="group-entry-header">
              <strong>${key}</strong>
              <button class="delete-group" data-key="${key}">Remove</button>
            </div>
            <div class="group-entry-values">
              ${values.map(val => `<span class="group-value-tag">${val}</span>`).join('')}
            </div>
          </div>
        `;
      }
    });
    
    if (!hasData) {
      html = '<p>No group data found for this user.</p>';
    }
    
    currentGroupsDiv.innerHTML = html;
    
    // Add event listeners to delete buttons
    document.querySelectorAll('.delete-group').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const key = e.target.dataset.key;
        removeUserGroup(key);
        e.preventDefault();
      });
    });
    
    if (showNotifications) utils.notify(`Group data for ${email} loaded successfully`, 'success');
  } catch (error) {
    console.error('Error loading group data:', error);
    currentGroupsDiv.innerHTML = `<p>Error: ${error.message}</p>`;
    if (showNotifications) utils.notify(`Error loading group data: ${error.message}`, 'error');
  } finally {
    if (getExistingGroupsBtn && providedEmail === null) {
      getExistingGroupsBtn.disabled = false;
    }
  }
}

async function addUserGroup() {
  const email = permEmailInput.value.trim();
  if (!email) {
    utils.notify('Please enter a user email', 'error');
    return;
  }
  
  if (!utils.isValidEmail(email)) {
    utils.notify('Please enter a valid email address', 'error');
    return;
  }
  
  const groupKey = groupKeySelect.value === 'custom' 
    ? customGroupKeyInput.value.trim() 
    : groupKeySelect.value;
    
  if (!groupKey) {
    utils.notify('Please select or enter a group key', 'error');
    return;
  }
  
  const groupValuesText = groupValuesInput.value.trim();
  if (!groupValuesText) {
    utils.notify('Please enter group values', 'error');
    return;
  }
  
  // Parse the comma-separated values
  const groupValues = groupValuesText.split(',')
    .map(value => value.trim())
    .filter(value => value !== '');
    
  if (groupValues.length === 0) {
    utils.notify('Please enter valid group values', 'error');
    return;
  }
  
  try {
    // Create the groups object with the key-values pair
    const groups = {
      [groupKey]: groupValues
    };
    
    // Update the user's groups
    await kuaApi.updateUserGroups(email, groups);
    
    utils.notify(`User ${email} has been added to the ${groupKey} group successfully`, 'success');
    
    // Refresh the permissions and groups
    await loadUserPermissions();
    await loadUserGroupData();
    
    // Clear the inputs
    groupValuesInput.value = '';
    if (groupKeySelect.value === 'custom') {
      customGroupKeyInput.value = '';
    }
    
  } catch (error) {
    console.error('Error adding user to group:', error);
    utils.notify(`Error adding user to group: ${error.message}`, 'error');
  }
}

async function removeUserGroup(key) {
  const email = permEmailInput.value.trim();
  
  if (!confirm(`Are you sure you want to remove the user from the ${key} group?`)) {
    return;
  }
  
  try {
    // Send an empty array for the given key to remove the user from that group
    const groups = {
      [key]: []
    };
    
    await kuaApi.updateUserGroups(email, groups);
    
    utils.notify(`User removed from ${key} group successfully`, 'success');
    
    // Refresh the permissions and groups
    await loadUserPermissions();
    await loadUserGroupData();
    
  } catch (error) {
    console.error('Error removing user from group:', error);
    utils.notify(`Error removing user from group: ${error.message}`, 'error');
  }
}

/**
 * Add a specific permission to a user
 * This works by finding which groups grant this permission and adding the user to that group
 */
async function addUserPermission() {
  const email = permEmailInput.value.trim();
  if (!email) {
    utils.notify('Please enter a user email', 'error');
    return;
  }
  
  if (!utils.isValidEmail(email)) {
    utils.notify('Please enter a valid email address', 'error');
    return;
  }
  
  const moduleCanonical = permissionModuleInput.value.trim();
  if (!moduleCanonical) {
    utils.notify('Please enter a module canonical name', 'error');
    return;
  }
  
  // Disable button to prevent multiple submissions
  addPermissionBtn.disabled = true;
  try {
    // Show loading indicator in UI and notify user
    permissionModuleInput.disabled = true;
    utils.notify(`Adding permission ${moduleCanonical}...`, 'info');
    
    // Debug: Get comprehensive info about user before changes
    console.log("--- BEFORE PERMISSION CHANGE ---");
    const beforeInfo = await kuaApi.getUserDebugInfo(email);
    console.log("User info before permission change:", beforeInfo);
    
    // Get existing permissions first to avoid redundant API calls
    const currentPermissions = beforeInfo.permissions;
    if (currentPermissions && currentPermissions[moduleCanonical]) {
      utils.notify(`User already has the ${moduleCanonical} permission`, 'info');
      permissionModuleInput.value = '';
      addPermissionBtn.disabled = false;
      permissionModuleInput.disabled = false;
      return;
    }
    
    // First, find which groups grant this permission
    const groupInfo = await kuaApi.findGroupsByModule(moduleCanonical);
    
    if (!groupInfo) {
      utils.notify(`No groups found that grant the ${moduleCanonical} permission`, 'error');
      addPermissionBtn.disabled = false;
      return;
    }
    
    // Add the user to the group that grants this permission
    const { key, values } = groupInfo;
    
    console.log(`Adding user to group with key=${key}, values=${JSON.stringify(values)}`);
    
    // Make a single API call to update groups
    const response = await kuaApi.updateUserGroups(email, { [key]: values });
    console.log("Group update API response:", response);
    
    // Wait a bit to let server caches clear
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Debug: Get comprehensive info about user after changes
    console.log("--- AFTER PERMISSION CHANGE ---");
    const afterInfo = await kuaApi.getUserDebugInfo(email);
    console.log("User info after permission change:", afterInfo);
    
    // Check if permission was actually added
    const hasPermissionNow = afterInfo.permissions && afterInfo.permissions[moduleCanonical];
    
    if (hasPermissionNow) {
      utils.notify(`Permission ${moduleCanonical} was added successfully!`, 'success');
    } else {
      // Permission wasn't added successfully with our first attempt
      // Let's try one more approach - using the application key directly
      try {
        utils.notify(`Trying alternative approach for adding ${moduleCanonical} permission...`, 'info');
        
        // Try with application key directly (this is common in the KUA system)
        const alternativeGroup = {
          'application': [moduleCanonical]
        };
        
        console.log(`Trying alternative group mapping: application=${moduleCanonical}`);
        
        // Make another API call with this alternative approach
        const altResponse = await kuaApi.updateUserGroups(email, alternativeGroup);
        console.log("Alternative group update API response:", altResponse);
        
        // Wait for potential caches to clear
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if it worked
        const finalInfo = await kuaApi.getUserDebugInfo(email);
        console.log("User info after alternative approach:", finalInfo);
        
        const hasPermissionAfterAlt = finalInfo.permissions && finalInfo.permissions[moduleCanonical];
        
        if (hasPermissionAfterAlt) {
          utils.notify(`Permission ${moduleCanonical} was added successfully using alternative approach!`, 'success');
          // Update UI with new permissions
          renderPermissionsList(finalInfo.permissions || {});
          displayGroupData(finalInfo.groups);
        } else {
          utils.notify(`Could not add permission ${moduleCanonical}. The backend may not be configured correctly for this permission.`, 'error');
        }
      } catch (altError) {
        console.error('Error with alternative permission approach:', altError);
        utils.notify(`Request to add permission ${moduleCanonical} was processed, but the permission is not showing up. This might be due to missing group configuration or cache delays.`, 'info');
      }
    }
    
    // Reload permissions data (use the most recent data we have)
    renderPermissionsList(afterInfo.permissions || {});
    
    // Load updated group data directly
    displayGroupData(afterInfo.groups);
    
    // Clear the input
    permissionModuleInput.value = '';
    
  } catch (error) {
    console.error('Error adding permission:', error);
    utils.notify(`Error adding permission: ${error.message}`, 'error');
  } finally {
    // Re-enable all controls
    addPermissionBtn.disabled = false;
    permissionModuleInput.disabled = false;
  }
}

// Helper function to display group data directly
function displayGroupData(groupsData) {
  let html = '';
  let hasData = false;
  
  Object.entries(groupsData).forEach(([key, values]) => {
    if (Array.isArray(values) && values.length > 0) {
      hasData = true;
      html += `
        <div class="group-entry" data-key="${key}">
          <div class="group-entry-header">
            <strong>${key}</strong>
            <button class="delete-group" data-key="${key}">Remove</button>
          </div>
          <div class="group-entry-values">
            ${values.map(val => `<span class="group-value-tag">${val}</span>`).join('')}
          </div>
        </div>
      `;
    }
  });
  
  if (!hasData) {
    html = '<p>No group data found for this user.</p>';
  }
  
  currentGroupsDiv.innerHTML = html;
  
  // Add event listeners to delete buttons
  document.querySelectorAll('.delete-group').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const key = e.target.dataset.key;
      removeUserGroup(key);
      e.preventDefault();
    });
  });
}

/**
 * Remove a specific permission from a user
 * This needs to find which groups grant this permission and remove the user from those groups
 */
async function removeUserPermission(permission) {
  const email = permEmailInput.value.trim();
  
  if (!confirm(`Are you sure you want to remove the ${permission} permission from this user?`)) {
    return;
  }
  
  // Disable the button to prevent multiple submissions
  const button = document.querySelector(`.permission-item button[data-permission="${permission}"]`);
  if (button) button.disabled = true;
  
  try {
    utils.notify(`Removing permission ${permission}...`, 'info');
    
    // Debug: Get comprehensive info about user before changes
    console.log("--- BEFORE PERMISSION REMOVAL ---");
    const beforeInfo = await kuaApi.getUserDebugInfo(email);
    console.log("User info before permission removal:", beforeInfo);
    
    // Check if user actually has the permission
    const hasPermission = beforeInfo.permissions && beforeInfo.permissions[permission];
    if (!hasPermission) {
      utils.notify(`User doesn't appear to have the ${permission} permission`, 'info');
      if (button) button.disabled = false;
      return;
    }
    
    // Try multiple approaches to remove the permission
    let permissionRemoved = false;
    
    // 1. First try our standard approach with findGroupsByModule
    try {
      // Find which groups grant this permission
      const groupInfo = await kuaApi.findGroupsByModule(permission);
      
      if (groupInfo) {
        // Remove the user from the group that grants this permission
        const { key } = groupInfo;
        const groups = {
          [key]: []
        };
        
        console.log(`[Approach 1] Removing user from group with key=${key}`);
        
        // Make API call to update groups
        const response = await kuaApi.updateUserGroups(email, groups);
        console.log("Group update API response:", response);
        
        // Wait for cache to clear
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if it worked
        const checkInfo = await kuaApi.getUserDebugInfo(email);
        const stillHasPermission = checkInfo.permissions && checkInfo.permissions[permission];
        
        if (!stillHasPermission) {
          permissionRemoved = true;
          utils.notify(`Permission ${permission} was removed successfully!`, 'success');
          renderPermissionsList(checkInfo.permissions || {});
          displayGroupData(checkInfo.groups);
        }
      }
    } catch (error) {
      console.error('Error with first removal approach:', error);
    }
    
    // 2. If first approach didn't work, try the 'application' key directly
    if (!permissionRemoved) {
      try {
        utils.notify(`Trying alternative approach for removing ${permission} permission...`, 'info');
        
        // Try with application key directly
        const alternativeGroup = {
          'application': []
        };
        
        console.log(`[Approach 2] Trying alternative removal: clearing application groups`);
        
        // Make another API call with this alternative approach
        const altResponse = await kuaApi.updateUserGroups(email, alternativeGroup);
        console.log("Alternative group update API response:", altResponse);
        
        // Wait for potential caches to clear
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if it worked
        const checkInfo = await kuaApi.getUserDebugInfo(email);
        const stillHasPermission = checkInfo.permissions && checkInfo.permissions[permission];
        
        if (!stillHasPermission) {
          permissionRemoved = true;
          utils.notify(`Permission ${permission} was removed successfully using alternative approach!`, 'success');
          renderPermissionsList(checkInfo.permissions || {});
          displayGroupData(checkInfo.groups);
        }
      } catch (altError) {
        console.error('Error with alternative removal approach:', altError);
      }
    }
    
    // 3. If permission still not removed, try a more targeted approach
    if (!permissionRemoved) {
      try {
        utils.notify(`Trying targeted approach for removing ${permission} permission...`, 'info');
        
        // Try with application key with specific value
        const targetedGroup = {
          'application': beforeInfo.groups['application'] ? 
            beforeInfo.groups['application'].filter(app => app !== permission) : 
            []
        };
        
        console.log(`[Approach 3] Trying targeted removal: removing specific value from application group`);
        console.log('Targeted group update:', targetedGroup);
        
        // Make another API call with this targeted approach
        const targetedResponse = await kuaApi.updateUserGroups(email, targetedGroup);
        console.log("Targeted group update API response:", targetedResponse);
        
        // Get final info after all attempts
        const finalInfo = await kuaApi.getUserDebugInfo(email);
        const stillHasPermission = finalInfo.permissions && finalInfo.permissions[permission];
        
        if (!stillHasPermission) {
          permissionRemoved = true;
          utils.notify(`Permission ${permission} was removed successfully using targeted approach!`, 'success');
        } else {
          utils.notify(`Could not remove permission ${permission}. It might be granted by a different group mechanism that we couldn't detect.`, 'warning');
        }
        
        // Update UI with the most recent data regardless of outcome
        renderPermissionsList(finalInfo.permissions || {});
        displayGroupData(finalInfo.groups);
      } catch (targetedError) {
        console.error('Error with targeted removal approach:', targetedError);
        utils.notify(`Error removing permission: ${targetedError.message}`, 'error');
      }
    }
    
  } catch (error) {
    console.error('Error removing permission:', error);
    utils.notify(`Error removing permission: ${error.message}`, 'error');
  } finally {
    // Re-enable button
    if (button) button.disabled = false;
  }
}

/**
 * Discover which groups would grant a specific permission
 * This is a debugging/admin utility to help understand the permission system
 */
async function discoverPermissionMapping() {
  const permissionName = discoverPermissionInput.value.trim();
  if (!permissionName) {
    utils.notify('Please enter a permission name to discover', 'error');
    return;
  }
  
  try {
    // Check if we have a valid API instance
    if (!kuaApi) {
      utils.notify('Please configure the API URL first', 'error');
      return;
    }
    
    // Disable button while processing
    discoverPermissionBtn.disabled = true;
    permissionDiscoveryResult.innerHTML = '<p>Analyzing permission mapping strategies...</p>';
    
    // Get all the possible strategies that could be used for this permission
    console.log(`Discovering mapping for permission: ${permissionName}`);
    
    // First, get the module-specific mappings from the API
    const specificMappings = await kuaApi._getModuleSpecificGroups(permissionName);
    
    // Then create common patterns that might be used
    const commonPatterns = [
      // Exact permission name with various keys
      { key: 'azure_ad_group', values: [permissionName] },
      { key: 'application', values: [permissionName] },
      { key: 'role', values: [permissionName] },
      
      // Common name patterns
      { key: 'azure_ad_group', values: [`${permissionName}-users`] },
      { key: 'azure_ad_group', values: [`${permissionName}-access`] },
      { key: 'azure_ad_group', values: [`${permissionName}-group`] },
      
      // Using as role
      { key: 'role', values: [`${permissionName}-user`] },
      { key: 'role', values: [`${permissionName}-admin`] },
      
      // Specific for landing-page pattern
      { key: 'application', values: [permissionName] }
    ];
    
    // Combine all strategies
    const allStrategies = [
      ...specificMappings,
      ...commonPatterns
    ];
    
    // Remove duplicates based on key and values equality
    const uniqueStrategies = [];
    const seen = new Set();
    
    allStrategies.forEach(strategy => {
      const key = `${strategy.key}:${strategy.values.join(',')}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueStrategies.push(strategy);
      }
    });
    
    // Sort strategies with known mappings first
    uniqueStrategies.sort((a, b) => {
      const aIsSpecific = specificMappings.some(m => 
        m.key === a.key && JSON.stringify(m.values) === JSON.stringify(a.values));
      const bIsSpecific = specificMappings.some(m => 
        m.key === b.key && JSON.stringify(m.values) === JSON.stringify(b.values));
      
      if (aIsSpecific && !bIsSpecific) return -1;
      if (!aIsSpecific && bIsSpecific) return 1;
      return 0;
    });
    
    // Generate HTML for discovery result
    const resultHtml = `
      <h5>Possible Group Mappings for "${permissionName}"</h5>
      <p>Here are the strategies that may grant this permission:</p>
      
      ${uniqueStrategies.map((strategy, index) => {
        const isKnownMapping = specificMappings.some(m => 
          m.key === strategy.key && JSON.stringify(m.values) === JSON.stringify(strategy.values));
        
        return `
          <div class="discovery-item">
            <h6>Strategy ${index + 1}${isKnownMapping ? ' (Known Mapping)' : ''}</h6>
            <div class="discovery-strategy">
              <pre>${JSON.stringify(strategy, null, 2)}</pre>
            </div>
            ${isKnownMapping ? 
              '<p><strong>This is a known mapping for this permission.</strong></p>' : 
              '<p>This is a common pattern that might work.</p>'}
          </div>
        `;
      }).join('')}
      
      <div class="info-message">
        <p><strong>How to use this information:</strong></p>
        <p>To grant this permission to a user, you'll need to add them to a group with one of these configurations. The entries marked as "Known Mapping" are most likely to work based on the system's common patterns.</p>
      </div>
    `;
    
    permissionDiscoveryResult.innerHTML = resultHtml;
    
  } catch (error) {
    console.error('Error discovering permission mapping:', error);
    permissionDiscoveryResult.innerHTML = `
      <div class="error-message">
        <h5>Error Discovering Permission Mapping</h5>
        <p>${error.message}</p>
      </div>
    `;
  } finally {
    // Re-enable button if it exists
    if (discoverPermissionBtn) {
      discoverPermissionBtn.disabled = false;
    }
    // Also scroll to the result if it exists
    if (permissionDiscoveryResult) {
      permissionDiscoveryResult.scrollIntoView({ behavior: 'smooth' });
    }
  }
}

/**
 * Compare permissions between two users to understand why one has permissions the other doesn't
 * This is a powerful debugging tool that helps admins understand permission issues
 */
async function compareUserPermissions() {
  const currentEmail = permEmailInput.value.trim();
  const compareEmail = compareEmailInput.value.trim();
  
  if (!currentEmail) {
    utils.notify('Please enter the current user email first', 'error');
    return;
  }
  
  if (!compareEmail) {
    utils.notify('Please enter an email to compare with', 'error');
    return;
  }
  
  if (!utils.isValidEmail(currentEmail) || !utils.isValidEmail(compareEmail)) {
    utils.notify('Please enter valid email addresses', 'error');
    return;
  }
  
  if (currentEmail === compareEmail) {
    utils.notify('Please enter a different email to compare with', 'error');
    return;
  }
  
  if (!kuaApi) {
    utils.notify('Please configure the API URL first', 'error');
    return;
  }
  
  try {
    // Disable button while processing
    comparePermissionsBtn.disabled = true;
    permissionsComparisonResult.innerHTML = '<p>Analyzing user permissions...</p>';
    
    // Get both users' data
    utils.notify(`Comparing permissions between ${currentEmail} and ${compareEmail}...`, 'info');
    
    // Get both users' debug info in parallel
    const [currentUserInfo, compareUserInfo] = await Promise.all([
      kuaApi.getUserDebugInfo(currentEmail),
      kuaApi.getUserDebugInfo(compareEmail)
    ]);
    
    console.log('Current user info:', currentUserInfo);
    console.log('Compare user info:', compareUserInfo);
    
    // Analyze differences
    const differences = analyzePermissionDifferences(currentUserInfo, compareUserInfo);
    
    if (differences.length === 0) {
      permissionsComparisonResult.innerHTML = `
        <div class="info-message">
          <h5>No Significant Differences Found</h5>
          <p>Both users have similar group configurations. If one user has permissions the other doesn't, 
          there might be other factors at play like role assignments or direct permissions.</p>
        </div>
      `;
      return;
    }
    
    // Generate HTML for the comparison result
    const comparisonHtml = `
      <h5>Permission Comparison Results</h5>
      <p>Differences between ${currentEmail} and ${compareEmail}:</p>
      
      ${differences.map((diff, index) => `
        <div class="comparison-item">
          <h6>Difference ${index + 1}: ${diff.type}</h6>
          <div class="${diff.type === 'Missing Group' ? 'group-difference' : 'group-match'}">
            <p><strong>Key:</strong> ${diff.key}</p>
            <p><strong>Values:</strong> ${JSON.stringify(diff.values)}</p>
            <p><strong>Affected Permissions:</strong> ${diff.affectedPermissions.join(', ') || 'Unknown'}</p>
            ${diff.type === 'Missing Group' ? `
              <button class="apply-difference-btn" data-key="${diff.key}" data-values='${JSON.stringify(diff.values)}'>
                Apply This Configuration
              </button>
            ` : ''}
          </div>
        </div>
      `).join('')}
      
      <div class="info-message">
        <p><strong>How to use this information:</strong></p>
        <p>The differences highlighted above show what group configurations might be missing for the current user.
        Click "Apply This Configuration" to add the missing group configuration to the current user.</p>
      </div>
    `;
    
    permissionsComparisonResult.innerHTML = comparisonHtml;
    
    // Add event listeners to apply difference buttons
    document.querySelectorAll('.apply-difference-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const key = e.target.dataset.key;
        const values = JSON.parse(e.target.dataset.values);
        
        try {
          e.target.disabled = true;
          e.target.textContent = 'Applying...';
          
          // Apply the group configuration
          const groups = { [key]: values };
          await kuaApi.updateUserGroups(currentEmail, groups);
          
          utils.notify(`Applied group configuration: ${key} = ${values.join(', ')}`, 'success');
          
          // Refresh user permissions to see if it worked
          await loadUserPermissions();
          
          // Disable the button to prevent duplicate applications
          e.target.disabled = true;
          e.target.textContent = 'Applied ✓';
          e.target.style.backgroundColor = '#4caf50';
          
        } catch (error) {
          console.error('Error applying group configuration:', error);
          utils.notify(`Error: ${error.message}`, 'error');
          
          e.target.disabled = false;
          e.target.textContent = 'Apply This Configuration';
        }
      });
    });
    
  } catch (error) {
    console.error('Error comparing user permissions:', error);
    permissionsComparisonResult.innerHTML = `
      <div class="error-message">
        <h5>Error Comparing Permissions</h5>
        <p>${error.message}</p>
      </div>
    `;
    utils.notify(`Error: ${error.message}`, 'error');
  } finally {
    // Re-enable button
    if (comparePermissionsBtn) {
      comparePermissionsBtn.disabled = false;
    }
  }
}

/**
 * Analyze differences between two users' permissions
 * 
 * @param {Object} currentUserInfo - Current user's debug info
 * @param {Object} compareUserInfo - Compare user's debug info
 * @returns {Array} Array of difference objects
 */
function analyzePermissionDifferences(currentUserInfo, compareUserInfo) {
  const differences = [];
  
  // Check if current user is missing groups that the compare user has
  if (compareUserInfo.groups && currentUserInfo.groups) {
    // For each group key in the compare user
    Object.entries(compareUserInfo.groups).forEach(([key, values]) => {
      // If it's an array with values
      if (Array.isArray(values) && values.length > 0) {
        // Check if current user has this key
        const currentValues = currentUserInfo.groups[key] || [];
        
        // Check which values are missing
        const missingValues = values.filter(value => !currentValues.includes(value));
        
        if (missingValues.length > 0) {
          // Try to determine which permissions are affected by this group
          const affectedPermissions = determineAffectedPermissions(compareUserInfo, key, missingValues);
          
          differences.push({
            type: 'Missing Group',
            key,
            values: missingValues,
            affectedPermissions
          });
        }
      }
    });
  }
  
  // Check for permission differences directly
  const currentPerms = currentUserInfo.permissions || {};
  const comparePerms = compareUserInfo.permissions || {};
  
  const missingPermissions = Object.keys(comparePerms).filter(perm => !currentPerms[perm]);
  
  if (missingPermissions.length > 0 && differences.length === 0) {
    // If we have missing permissions but couldn't find group differences, add a direct permission entry
    differences.push({
      type: 'Missing Permissions',
      key: 'application',
      values: missingPermissions,
      affectedPermissions: missingPermissions
    });
  }
  
  return differences;
}

/**
 * Try to determine which permissions are affected by a specific group configuration
 * 
 * @param {Object} userInfo - User's debug info
 * @param {string} key - Group key
 * @param {Array} values - Group values
 * @returns {Array} Array of affected permission names
 */
function determineAffectedPermissions(userInfo, key, values) {
  // This is a heuristic approach since we don't have direct access to the permission mapping
  const permissions = Object.keys(userInfo.permissions || {});
  
  if (key === 'application') {
    // For application key, the values often directly match the permission names
    return values.filter(value => permissions.includes(value));
  }
  
  if (key === 'azure_ad_group' || key === 'ldap_group') {
    // For AD groups, try to match based on name patterns
    return permissions.filter(perm => {
      return values.some(value => 
        value === perm || 
        value === `${perm}-users` || 
        value === `${perm}-group` ||
        value.includes(perm)
      );
    });
  }
  
  // For other keys, we can't easily determine affected permissions
  return [];
}

/**
 * Toggle between light and dark theme
 */
function toggleTheme() {
  const isDarkTheme = document.body.classList.toggle('dark-theme');
  updateThemeToggleButton(isDarkTheme);
  
  // Save theme preference to localStorage
  localStorage.setItem('kuaAdminTheme', isDarkTheme ? 'dark' : 'light');
}

/**
 * Update the theme toggle button appearance
 * @param {boolean} isDarkTheme - Whether the current theme is dark
 */
function updateThemeToggleButton(isDarkTheme) {
  const themeToggleBtn = document.getElementById('theme-toggle');
  if (!themeToggleBtn) return;
  
  const buttonText = themeToggleBtn.querySelector('span');
  const buttonIcon = themeToggleBtn.querySelector('svg');
  
  if (isDarkTheme) {
    buttonText.textContent = 'Light Mode';
    // Replace with sun icon
    buttonIcon.innerHTML = '<circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>';
  } else {
    buttonText.textContent = 'Dark Mode';
    // Replace with moon icon
    buttonIcon.innerHTML = '<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>';
  }
}

// Initialize the application
document.addEventListener('DOMContentLoaded', init);